import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../../../../layout/Layout";
import MarketplaceLinks from "./MarketplaceLinks";
import { BOOK } from "./storeConfig";
import cover from "../../../../assets/one-person-ten-people-cover.png";
import "animate.css";

const faqs = [
  {
    q: "What format do I receive?",
    a: "A typeset PDF, 6×9 digital-first trade format, built for reading on a laptop, tablet, or phone. You buy it on Etsy, Gumroad, or Amazon, and that store sends you the file.",
  },
  {
    q: "When do I get the book?",
    a: "Immediately after you pay on the marketplace. They handle checkout and the download. This site is the sales page, not the store.",
  },
  {
    q: "Is this a list of AI tools?",
    a: "No. Products change every few months. The book is organised around eight stations and a verification discipline that outlives any named model.",
  },
  {
    q: "Do I need to be a founder?",
    a: "No. It is for anyone who now has to research, write, build, analyse, and sell without a team standing behind them — including specialists and operators inside companies.",
  },
];

function SalesPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const [showSticky, setShowSticky] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const onScroll = () => setShowSticky(window.scrollY > 520);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <Layout>
      <div className="bg-white pb-24">
        <section className="bg-[#0d1117] text-white pt-16 pb-16 md:pt-20 md:pb-20 px-6 md:px-12">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <p className="text-sm text-slate-400">
                <Link to={BOOK.hubPath} className="hover:text-white">
                  Digital Products
                </Link>
                <span className="mx-2">/</span>
                <Link to={BOOK.ebooksPath} className="hover:text-white">
                  eBooks
                </Link>
              </p>
              <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#d4694e]">
                {BOOK.kicker}
              </p>
              <h1 className="text-4xl md:text-5xl xl:text-6xl font-extrabold leading-tight tracking-tight">
                One person.
                <br />
                The coverage of{" "}
                <span className="text-[#d4694e]">ten.</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-300 max-w-xl">
                {BOOK.subtitle} — without outsourcing the only part that still
                matters: your judgement.
              </p>
              <div className="flex flex-col items-start gap-3">
                <MarketplaceLinks light />
                <p className="text-sm text-slate-400">
                  Buy on the store you already use · Instant PDF from them
                </p>
              </div>
              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-white/10 text-sm text-slate-400">
                <p>
                  <strong className="block text-white text-base">
                    {BOOK.pages} pages
                  </strong>
                  6×9 field manual
                </p>
                <p>
                  <strong className="block text-white text-base">
                    {BOOK.chapters} chapters
                  </strong>
                  Prompts & worksheets
                </p>
                <p>
                  <strong className="block text-white text-base">
                    {BOOK.author}
                  </strong>
                  {BOOK.edition}
                </p>
              </div>
            </div>

            <div className="flex justify-center">
              <img
                src={cover}
                alt={`${BOOK.title} cover`}
                className="w-[230px] md:w-[280px] h-auto rounded-md shadow-2xl border border-white/10"
              />
            </div>
          </div>
        </section>

        <section className="bg-gray-50 py-10 px-6">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-sm text-gray-700">
            <p>
              <strong className="block text-black mb-1">Science, 2023.</strong>
              Writing tasks with ChatGPT: ~40% faster, quality up 18%.
            </p>
            <p>
              <strong className="block text-black mb-1">BCG field experiment.</strong>
              Inside the model’s competence: more work, faster, higher quality.
            </p>
            <p>
              <strong className="block text-black mb-1">METR, 2025.</strong>
              Experienced developers on their own repos: 19% slower with AI.
            </p>
            <p>
              <strong className="block text-black mb-1">The pattern.</strong>
              Your gain is largest where you are weakest — and can reverse where
              you are expert.
            </p>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-6 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <h2 className="text-3xl md:text-4xl leading-tight">
            AI collapsed the cost of building. It did not collapse the cost of
            being wrong.
          </h2>
          <div className="space-y-6 text-gray-700 text-md leading-relaxed">
            <p>
              A person working alone now produces, in a week, output that a
              decade ago required a funded team. That leverage is real. The trap
              is also real: the work comes out plausible — formatted, confident,
              fluent, and wrong in ways a solo operator is unequipped to catch.
            </p>
            <div className="space-y-4">
              {[
                {
                  t: "You are blind in nine directions.",
                  d: "A team is not ten pairs of hands. It is ten different things being watched for. Alone, the missing roles produce no signal.",
                },
                {
                  t: "The artefact improves while you do not.",
                  d: "MIT and education studies show weaker recall and metacognitive laziness when the model does the thinking.",
                },
                {
                  t: "Speed without conversion is a rounding error.",
                  d: "Danish labour data found near-zero effect on earnings two years after ChatGPT. Having the tool is not the work.",
                },
              ].map((item) => (
                <div
                  key={item.t}
                  className="bg-white p-6 rounded-xl shadow-md border border-gray-100"
                >
                  <p className="font-semibold text-black mb-2">{item.t}</p>
                  <p>{item.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#0f2e36] text-white px-6 py-20">
          <div className="max-w-4xl mx-auto space-y-6">
            <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#e8a090]">
              What this book actually claims
            </p>
            <h2 className="text-3xl md:text-4xl leading-tight">
              Coverage, not a fantasy payroll.
            </h2>
            <p className="text-lg text-slate-200">
              You can now cover the ground a ten-person team used to cover. You
              cannot outsource the judgement that decides whether the ground was
              worth covering, or whether the covering was done correctly.
            </p>
            <p className="border-l-4 border-[#d4694e] pl-5 text-lg italic text-slate-100">
              Those two facts define the job. This book is the operating system
              for that job: eight stations, ten roles, and the verification
              habits that keep a one-person company from shipping confident
              error at scale.
            </p>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="text-3xl md:text-4xl mb-4">
            Eight stations. One operator.
          </h2>
          <p className="text-gray-600 mb-10 max-w-2xl">
            AI does the production. You keep the judging. Enter wherever the live
            problem is.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              ["01", "Research", "Find out what is actually true — primary sources, not folklore."],
              ["02", "Learn", "Install it in yourself. Retrieval, not rereading."],
              ["03", "Think", "Decide, and be able to say why. Pre-mortem the plan."],
              ["04", "Write", "Make the thought hold its shape on the page."],
              ["05", "Build", "Ship the working thing. Boring, fail-loud systems."],
              ["06", "Analyse", "Read what came back. Refuse vanity metrics."],
              ["07", "Automate", "Refuse to do it twice. Compounded reliability."],
              ["08", "Sell", "Find who it is for. Proof-of-work distribution."],
            ].map(([n, title, body]) => (
              <article
                key={title}
                className="bg-white p-6 rounded-2xl shadow-md border border-gray-100 min-h-[160px]"
              >
                <p className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">
                  {n}
                </p>
                <h3 className="text-xl mb-2">{title}</h3>
                <p className="text-gray-600 text-sm">{body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="bg-gray-50 px-6 py-20">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl mb-10">Inside the eBook</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <article className="bg-white p-8 rounded-2xl shadow-md">
                <p className="text-xs font-semibold tracking-widest uppercase text-slate-500">
                  Part I
                </p>
                <h3 className="text-2xl mt-2 mb-3">The Ground Truth</h3>
                <ul className="text-gray-700 space-y-2 text-sm list-disc pl-4">
                  <li>The Leverage Is Real</li>
                  <li>The Ten People Inside One Person</li>
                  <li>Cognitive Debt</li>
                  <li>Taste Is the Bottleneck</li>
                </ul>
              </article>
              <article className="bg-white p-8 rounded-2xl shadow-md">
                <p className="text-xs font-semibold tracking-widest uppercase text-slate-500">
                  Part II
                </p>
                <h3 className="text-2xl mt-2 mb-3">The Loop</h3>
                <p className="text-gray-700 text-sm">
                  Each station: what to hand over, what to keep, the failure
                  mode, a drill, and prompts. Research through Sell.
                </p>
              </article>
              <article className="bg-white p-8 rounded-2xl shadow-md">
                <p className="text-xs font-semibold tracking-widest uppercase text-slate-500">
                  Part III + extras
                </p>
                <h3 className="text-2xl mt-2 mb-3">The Operating System</h3>
                <ul className="text-gray-700 space-y-2 text-sm list-disc pl-4">
                  <li>Daily cadence, sovereign stack, 90-day plan</li>
                  <li>18-prompt library and verification playbook</li>
                  <li>Worksheets and sourced notes</li>
                </ul>
              </article>
            </div>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="text-3xl md:text-4xl mb-8">What you get</h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
            {BOOK.previewPoints.concat([
              "Every empirical claim sourced at the back of the book",
              "Instant PDF from Etsy, Gumroad, or Amazon — read on any device",
            ]).map((item) => (
              <li
                key={item}
                className="flex gap-3 bg-white p-5 rounded-xl shadow-md border border-gray-100"
              >
                <span className="font-semibold">→</span>
                <span className="text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center justify-between gap-4 p-8 bg-gray-50 rounded-2xl border border-gray-200">
            <div>
              <p className="text-2xl">Get the field manual.</p>
              <p className="text-gray-500 text-sm mt-1">
                Choose Etsy, Gumroad, or Amazon · Instant PDF from the store
              </p>
            </div>
            <MarketplaceLinks />
          </div>
        </section>

        <section className="bg-gray-50 px-6 py-20">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
            <article className="bg-white p-8 rounded-2xl shadow-md">
              <h3 className="text-2xl mb-4">This is yours if</h3>
              <ul className="space-y-3 text-gray-700 list-disc pl-5">
                <li>
                  You ship work you could not have produced alone three years
                  ago — and you are not always sure how you knew it was correct
                </li>
                <li>
                  You are a founder, freelancer, or specialist covering roles
                  you never trained for
                </li>
                <li>
                  You want evidence with names, papers, and limitations — not a
                  tool list that expires
                </li>
              </ul>
            </article>
            <article className="bg-white p-8 rounded-2xl shadow-md">
              <h3 className="text-2xl mb-4">This is not yours if</h3>
              <ul className="space-y-3 text-gray-700 list-disc pl-5">
                <li>You want permission to skip learning</li>
                <li>
                  You want a promise of a one-person billion-dollar company
                </li>
                <li>You want prompts you can paste without reading the answer</li>
              </ul>
            </article>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="text-3xl md:text-4xl mb-10">Ninety days after you buy</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              ["Days 1–30", "Foundation", "Attention hygiene, freeze the stack, run all eight stations on a tiny topic, then measure cognitive debt."],
              ["Days 31–60", "Micro-offer", "Find a painful commercial problem, ship proof of work, and get the first yes without burning the runway."],
              ["Days 61–90", "Harden the system", "Convert what worked, kill what did not, and make the loop repeatable."],
            ].map(([k, t, d]) => (
              <article key={t} className="bg-white p-8 rounded-2xl shadow-md border-t-4 border-black">
                <p className="text-xs font-semibold tracking-widest uppercase text-slate-500">
                  {k}
                </p>
                <h3 className="text-2xl mt-2 mb-3">{t}</h3>
                <p className="text-gray-700 text-sm">{d}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-3xl mb-6">Before you buy</h2>
          <div className="divide-y divide-gray-200 border-t border-b border-gray-200">
            {faqs.map((item, index) => (
              <div key={item.q}>
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full text-left py-4 flex justify-between gap-4 text-lg font-medium"
                >
                  {item.q}
                  <span>{openFaq === index ? "–" : "+"}</span>
                </button>
                {openFaq === index && (
                  <p className="pb-4 text-gray-600 max-w-3xl">{item.a}</p>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="bg-[#0d1117] text-white text-center px-6 py-20">
          <div className="max-w-3xl mx-auto space-y-6">
            <h2 className="text-3xl md:text-4xl leading-tight">
              One person can now do the work of ten. Whether that is value or
              well-formatted error depends on you.
            </h2>
            <div className="flex justify-center">
              <MarketplaceLinks light />
            </div>
          </div>
        </section>
      </div>

      {showSticky && (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-black/95 text-white px-4 py-3 border-t border-white/10">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
            <span className="text-sm md:text-base truncate">{BOOK.title}</span>
            <MarketplaceLinks compact light />
          </div>
        </div>
      )}
    </Layout>
  );
}

export default SalesPage;
